import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

import { LandingCard } from "./LandingPrimitives";
import { solutionBullets } from "./landingContent";

const TUTORIAL_VIDEO_SRC = "/videos/tutorial.mp4";

export default function LandingSolutionSection() {
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches,
  );
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (!tutorialOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setTutorialOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [tutorialOpen]);

  return (
    <section id="solutia" className="landing-section">
      <div className="landing-shell">
        <div className="landing-solution-layout">
          <div>
            <div className="landing-section__head landing-section__head--compact">
              <p className="landing-section__eyebrow">Soluția</p>
              <h2>
                AIFoiParcurs completează automat perioadele lipsă la foile de parcurs
              </h2>
              <p>
                Aplicația se adaptează situației firmei tale, indiferent dacă pornești de la zero, ai
                restanțe sau vrei să automatizezi completarea foilor de parcurs.
              </p>
            </div>

            <div className="landing-solution-bullets">
              {solutionBullets.map((bullet) => (
                <LandingCard key={bullet.title} className="landing-solution-bullet">
                  <div className="landing-solution-bullet__row">
                    <div className="landing-solution-bullet__dot" />
                    <div>
                      <p className="landing-solution-bullet__title">{bullet.title}</p>
                      <p className="landing-solution-bullet__description">{bullet.description}</p>
                    </div>
                  </div>
                </LandingCard>
              ))}
            </div>
          </div>

          <div className="landing-motion-stage">
            <div className="landing-orb landing-orb--violet landing-orb--solution-left" />
            <div className="landing-orb landing-orb--sky landing-orb--solution-right" />

            <motion.div
              animate={
                isMobile
                  ? { rotateX: 0, rotateY: 0, y: 0 }
                  : { rotateX: [7, 4, 7], rotateY: [-6, -3, -6], y: [0, -5, 0] }
              }
              transition={
                isMobile
                  ? { duration: 0 }
                  : { duration: 8.5, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }
              }
              className="landing-motion-card"
            >
              <LandingCard glow className="landing-solution-card">
                <div className="landing-solution-card__surface">
                  <div className="landing-solution-card__header">
                    <div>
                      <p className="landing-solution-card__caption">Cum funcționează în practică</p>
                      <p className="landing-solution-card__title">
                        După primii pași simpli, totul devine rapid și consistent
                      </p>
                    </div>
                    <div className="landing-solution-card__pill">flux clar</div>
                  </div>

                  <div className="landing-solution-card__grid">
                    <div className="landing-inset-panel">
                      <p className="landing-inset-panel__caption">Prima completare și validare</p>
                      <div className="landing-inset-panel__stack">
                        <div className="landing-soft-panel">
                          <p className="landing-soft-panel__title">Locații frecvente</p>
                          <p>
                            Introduse o singură dată pentru utilizarea uzuală și folosite la
                            completările următoare.
                          </p>
                        </div>
                        <div className="landing-soft-panel">
                          <p className="landing-soft-panel__title">Alimentările</p>
                          <p>
                            Pentru prima completare automată ajunge și o singură alimentare recentă.
                            Alimentările din urmă se pot importa automat din fișa de combustibil.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="landing-soft-panel landing-soft-panel--column">
                      <p className="landing-inset-panel__caption">Cazuri specifice</p>
                      <div className="landing-inset-panel__stack">
                        <div className="landing-soft-panel">
                          <p className="landing-soft-panel__title">Trasee manuale când este nevoie</p>
                          <p>
                            Poți introduce anumite trasee manual atunci când apar deplasări în afara
                            tiparului obișnuit.
                          </p>
                        </div>
                        <div className="landing-soft-panel">
                          <p className="landing-soft-panel__title">Consum mediu realist</p>
                          <p>
                            Dacă kilometrii reali nu corespund cu litrii consumați, există soluții în
                            limita unui consum mediu realist.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="landing-solution-summary">
                    <p className="landing-solution-card__caption">După prima utilizare</p>
                    <p className="landing-solution-summary__title">Mai puțini pași</p>
                    <p>
                      Introduci ultimele alimentări, actualizezi locațiile frecvente doar dacă e
                      cazul, iar restul rămâne în seama algoritmului AIFoiParcurs.
                    </p>
                  </div>
                </div>
              </LandingCard>
            </motion.div>
          </div>
        </div>

        <button
          type="button"
          className="landing-tutorial-card"
          onClick={() => setTutorialOpen(true)}
        >
          <span className="landing-tutorial-card__thumb-wrap" aria-hidden="true">
            <img
              src="/videos/tutorial-foi-parcurs.png"
              alt="Tutorial video AIFoiParcurs: completarea foilor de parcurs"
              className="landing-tutorial-card__thumb"
            />
          </span>
          <span className="landing-tutorial-card__body">
            <span className="landing-tutorial-card__eyebrow">Tutorial video</span>
            <span className="landing-tutorial-card__title">Vezi cum folosești aplicația</span>
            <span className="landing-tutorial-card__text">
              De la cont și vehicul, până la locații, alimentări și completarea automată a foilor de
              parcurs.
            </span>
            <span className="landing-tutorial-card__cta">
              Deschide tutorialul
              <span className="landing-tutorial-card__cta-arrow" aria-hidden="true">
                →
              </span>
            </span>
          </span>
        </button>
      </div>

      {tutorialOpen ? (
        <div
          className="landing-tutorial-modal"
          role="presentation"
          onClick={() => setTutorialOpen(false)}
        >
          <div
            className="landing-tutorial-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="landing-tutorial-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="landing-tutorial-modal__header">
              <h3 id="landing-tutorial-title">Tutorial AIFoiParcurs</h3>
              <button
                ref={closeButtonRef}
                type="button"
                className="landing-tutorial-modal__close"
                onClick={() => setTutorialOpen(false)}
              >
                Închide
              </button>
            </div>
            <div className="landing-tutorial-modal__video">
              <video
                controls
                autoPlay
                playsInline
                preload="metadata"
                src={TUTORIAL_VIDEO_SRC}
              >
                Browserul tău nu poate reda acest video.
              </video>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
